import React, { FC, useEffect, useState } from 'react';
import { AutoComplete, Button, Drawer, Form, Input } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { WarningCard } from 'common/components/WarningCard';
import { EPlaceholder } from 'common/const/placeholder.enum';
import { debounce } from 'common/helpers/common.helper';
import { renderRequiredMark, rules } from 'common/helpers/form.helper';
import { SELECT_LIST_HEIGHT_320 } from 'common/config';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { ReactComponent as CloseIcon } from 'app/assets/images/redesign/close.svg';
import { IAddress, ICreateAddressPayload } from 'entities/Address/Address.models';
import { getAddressSuggestionOptions } from 'entities/Address/Address.helper';
import { getAddressSuggestions, selectAddressSuggestions, setAddressSuggestions } from 'entities/Address/Address.slice';

interface IComponentProps {
  open: boolean;
  loading: boolean;
  address?: IAddress | null;
  onCancel: () => void;
  onSubmit: (payload: ICreateAddressPayload) => void;
}

export const AddressCreateCard: FC<IComponentProps> = ({ open, loading, onCancel, onSubmit, address }) => {
  const dispatch = useAppDispatch();

  const addressSuggestions = useAppSelector(selectAddressSuggestions);

  const [formNotSaved, setFormNotSaved] = useState<boolean>(false);
  const [openWarningCard, setOpenWarningCard] = useState<boolean>(false);
  const [form] = useForm();

  useEffect(() => {
    if (open && address) {
      form.setFieldsValue({
        cityId: address.cityId,
        name: address.name,
        city: address.city.name,
        postalCode: address.postalCode,
      });
    }
  }, [open, address]);

  const isEdit = !!address;
  const addressSuggestionOptions = getAddressSuggestionOptions(addressSuggestions);

  const debouncedSearch = debounce((value: string) => dispatch(getAddressSuggestions({ name: value })));

  const onAddressSelect = (value: string) => {
    const address = addressSuggestions.find((item) => item.name === value);

    if (address) {
      setFormNotSaved(true);
      form.setFieldsValue({
        cityId: address.cityId,
        name: address.name,
        city: address.city,
        postalCode: address.postalCode,
      });

      if (!address.cityId) {
        form.setFields([{ name: 'suggestions', errors: ['Продолжайте ввод — нужно выбрать конкретный адрес.'] }]);
      }
    }
  };

  const closeCard = () => {
    onCancel();
    setOpenWarningCard(false);
    form.resetFields();
    dispatch(setAddressSuggestions([]));
    setFormNotSaved(false);
  };

  const handleCancel = () => {
    if (loading) {
      return;
    }

    if (formNotSaved) {
      setOpenWarningCard(true);
      return;
    }

    closeCard();
  };

  const onFormValuesChange = () => {
    setFormNotSaved(true);
  };

  const handleSubmit = ({ name, cityId, postalCode }: ICreateAddressPayload) => {
    if (!cityId) {
      form.setFields([{ name: 'suggestions', errors: ['Продолжайте ввод — нужно выбрать конкретный адрес.'] }]);
      return;
    }

    onSubmit({ name: name.replaceAll('\n', ' '), cityId, postalCode, onSuccess: closeCard });
  };

  return (
    <>
      <Drawer
        rootClassName="redesign drawer address-create-card"
        destroyOnClose
        width={720}
        open={open}
        onClose={handleCancel}
        footer={
          <Button className="button-lg primary" onClick={form.submit} loading={loading}>
            {isEdit ? 'Сохранить изменения' : 'Добавить адрес'}
          </Button>
        }
      >
        <div className="drawer__body">
          <div className="drawer__title mb-14">{isEdit ? 'Редактировать адрес' : 'Новый адрес'}</div>

          <Form
            form={form}
            layout="vertical"
            requiredMark={renderRequiredMark}
            onValuesChange={onFormValuesChange}
            onFinish={handleSubmit}
          >
            <Form.Item label="Автоматический поиск" name="suggestions">
              <AutoComplete
                rootClassName="redesign"
                popupClassName="redesign"
                className="redesign autosize-autocomplete"
                virtual={false}
                listHeight={SELECT_LIST_HEIGHT_320}
                allowClear={{ clearIcon: <CloseIcon className="icon-close-input" /> }}
                onChange={debouncedSearch}
                options={addressSuggestionOptions}
                onSelect={onAddressSelect}
              >
                <Input.TextArea placeholder={EPlaceholder.StartTypingAddress} autoSize />
              </AutoComplete>
            </Form.Item>
            <Form.Item name="cityId" hidden>
              <Input />
            </Form.Item>

            <Form.Item className="mb-9" label="Адрес" name="name" rules={[rules.required()]}>
              <Input.TextArea
                className="autosize-input"
                placeholder={EPlaceholder.SpecifyAddress}
                autoSize
                allowClear={{ clearIcon: <CloseIcon className="icon-close-input" /> }}
              />
            </Form.Item>

            <Form.Item className="mb-9" label="Город" name="city" rules={[rules.required()]}>
              <Input
                placeholder={EPlaceholder.SpecifyCity}
                allowClear={{ clearIcon: <CloseIcon className="icon-close-input" /> }}
              />
            </Form.Item>

            <Form.Item className="mb-9" label="Индекс" name="postalCode" rules={[rules.required()]}>
              <Input
                placeholder={EPlaceholder.SpecifyPostalCode}
                allowClear={{ clearIcon: <CloseIcon className="icon-close-input" /> }}
              />
            </Form.Item>
          </Form>
        </div>
      </Drawer>

      <WarningCard
        open={openWarningCard}
        content="Вы уверены, что не хотите сохранить изменения?"
        subtitle="Введенные данные будут потеряны."
        confirmBtnTitle="Не сохранять"
        onConfirm={closeCard}
        onClose={() => setOpenWarningCard(false)}
      />
    </>
  );
};
